import React from 'react';
import { Select, MenuItem, FormControl, InputLabel, IconButton } from '@mui/material';
import { chartSelectStyles } from 'components/charts/MainChart';
import { ReactComponent as CaretCircleDown } from '../../assets/icons/CaretCircleDown.svg'
import ClearIcon from '@mui/icons-material/Clear';

export type YearRange = {
  start: number | null;
  end: number | null;
};

interface YearPickerProps {
  yearRange: YearRange;
  setYearRange: React.Dispatch<React.SetStateAction<YearRange>>;
  label: string;
  isStartYear: boolean;
}

const YearPicker: React.FC<YearPickerProps> = ({ yearRange, setYearRange, label, isStartYear }) => {
  const currentYear = new Date().getFullYear();
  const numberOfYears = currentYear - 1999; // 2000 through current year
  const years = Array.from({ length: numberOfYears }, (_, index) => currentYear - index);

  const value = isStartYear ? yearRange.start : yearRange.end;
  const otherValue = isStartYear ? yearRange.end : yearRange.start;

  const handleChange = (year: number) => {
    setYearRange(prev => ({
      ...prev,
      [isStartYear ? 'start' : 'end']: year
    }));
  };

  const handleClear = () => {
    setYearRange(prev => ({
      ...prev,
      [isStartYear ? 'start' : 'end']: null
    }));
  };

  // Ensures input label is centered when inactive and above component when in use  
  const inputLabelStyle = {
    '&.MuiInputLabel-root': {
      transform: value ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, 20px) scale(1)',
      transition: 'transform 200ms ease-out',
    },
    '&.MuiInputLabel-root[data-shrink="true"]': value ? { display: 'none' } : {},
  }

  const isYearDisabled = (year) => {
    if (isStartYear) {
      return otherValue !== null && year > otherValue;
    } else {
      return otherValue !== null && year < otherValue;
    }
  };

  return (
    <FormControl>
      <InputLabel sx={inputLabelStyle} id={`${label}-select-label`} disableAnimation>{label}</InputLabel>
      <Select
        labelId={`${label}-select-label`}
        id={`${label}-select`}
        value={value}
        onChange={(e) => handleChange(Number(e.target.value))}
        sx={{
          width: 150, ...chartSelectStyles,
          '& .MuiSelect-select': {
            padding: '0px !important',
          }
        }}
        IconComponent={CaretCircleDown}
        endAdornment={
          <>
            {value !== null && (
              <IconButton
                onClick={handleClear}
                sx={{ padding: 0, marginRight: '30px' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </>
        }
      >
        {years.map((year) => (
          <MenuItem
            key={year}
            value={year}
            disabled={isYearDisabled(year)}
          >
            {year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface YearRangePickerProps {
  yearRange: YearRange;
  setYearRange: React.Dispatch<React.SetStateAction<YearRange>>;
}

const YearRangePicker: React.FC<YearRangePickerProps> = ({ yearRange, setYearRange }) => {
  return (
    <FormControl sx={{ m: 1, width: 330 }}>
      <InputLabel htmlFor="grouped-native-select" shrink>Range of Life Cycle Assessment Years</InputLabel>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <YearPicker
          yearRange={yearRange}
          setYearRange={setYearRange}
          label="Start Year"
          isStartYear={true}
        />
        <span style={{ display: 'flex', alignItems: 'center', marginTop: 15, fontWeight: 'bolder' }}>-</span>
        <YearPicker
          yearRange={yearRange}
          setYearRange={setYearRange}
          label="End Year"
          isStartYear={false}
        />
      </div>
    </FormControl>
  );
};

export default YearRangePicker;